import { informationListOptions, InformationReadable } from '@crm/api'
import {
  ActionInformationModal,
  DeleteInformation
} from '@crm/features/information/components'
import { useRolePermission } from '@crm/hooks/useRolePermission'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { UserRoleCRM } from '@crm/types'
import { defaultFilters } from '@crm/utils'
import { TableActions, TableContainer, TitleSection } from '@shared/components'
import { useQueryParams } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { useQuery } from '@tanstack/react-query'
import { Button, MenuProps, TableColumnsType } from 'antd'
import { Edit2, Trash } from 'iconsax-react'
import { useMemo, useState } from 'react'

export const Information = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const sorter = useTableSorter()

  const allowEdit = useRolePermission([UserRoleCRM.admin])

  const [isCreateEditOpen, setIsCreateEditOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [itemId, setItemId] = useState<number | null>(null)

  const getActionColumnItem: (id: number) => MenuProps['items'] = (id) => [
    {
      label: t('information.edit'),
      key: 'edit',
      icon: <Edit2 size={20} />,
      onClick: () => {
        setItemId(id)
        setIsCreateEditOpen(true)
      }
    },
    { type: 'divider' },
    {
      label: t('information.delete'),
      key: 'delete',
      icon: <Trash size={20} />,
      danger: true,
      onClick: () => {
        setItemId(id)
        setIsDeleteOpen(true)
      }
    }
  ]

  const columns = useMemo(
    (): TableColumnsType<InformationReadable> => [
      {
        title: t('information.title'),
        sorter: true,
        dataIndex: 'title',
        key: 'title'
      },
      {
        title: t('information.description'),
        dataIndex: 'description',
        key: 'description'
      },
      ...(allowEdit
        ? [
            {
              width: 50,
              fixed: 'right' as const,
              render: ({ id }: InformationReadable) => (
                <TableActions options={getActionColumnItem(id)} />
              )
            }
          ]
        : [])
    ],
    [t, allowEdit]
  )

  const { data, isLoading } = useQuery({
    ...informationListOptions({ query: { ...defaultFilters, ...params } })
  })
  const onActionsClose = () => {
    setIsCreateEditOpen(false)
    setIsDeleteOpen(false)
    setItemId(null)
  }

  return (
    <>
      <TitleSection title={t(`menu.information`)} count={data?.count} />

      <TableContainer
        loading={isLoading}
        data={data}
        columns={columns}
        onChange={(_, __, result) => sorter(result)}
        showSorterTooltip={false}
        rightChildren={
          allowEdit && (
            <Button type="primary" onClick={() => setIsCreateEditOpen(true)}>
              {t('information.add_information')}
            </Button>
          )
        }
      />

      <ActionInformationModal
        id={itemId}
        isOpen={isCreateEditOpen}
        onClose={onActionsClose}
      />

      <DeleteInformation
        id={itemId}
        isOpen={isDeleteOpen}
        onClose={onActionsClose}
      />
    </>
  )
}
